import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Provider Choice — the patient portal's "Provider & day" step (docs/GLOSSARY.md, "Patient
 * booking"). Patient-only: lists every clinician
 * (`PractitionerRole.code` 112247003) who passes State Match for the caller, with the
 * number of Bookable Slots per calendar day in `[from, to)`. Providers with no Bookable
 * Slot in the window are still listed (the portal hides them behind "Show all providers")
 * and sort to the tail.
 *
 * `timeZone` is the first place a client hands its IANA zone to the middleware: the
 * server groups slots by calendar day in that zone, so a slot that straddles midnight
 * lands on different `date` keys for different zones. Ranking and the zero-slot tail are
 * zone-independent.
 *
 * Flat provider blocks only — never FHIR resources. `photoUrl` is the raw
 * `Practitioner.photo[0].url`; the browser resolves it through Medplum as Get Care does.
 */

/** Two whole months from any start date fit in 62 days; a wider window is a client bug. */
export const MAX_BOOKABLE_WINDOW_DAYS = 62

export function isKnownTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch {
    return false
  }
}

export const listBookableProvidersInput = z
  .object({
    /** ISO-8601 instant, inclusive. */
    from: z.string().datetime(),
    /** ISO-8601 instant, exclusive. */
    to: z.string().datetime(),
    /** IANA zone the per-day counts are keyed in (e.g. `America/New_York`). */
    timeZone: z.string(),
  })
  .superRefine((input, ctx) => {
    const from = new Date(input.from).getTime()
    const to = new Date(input.to).getTime()
    if (to <= from) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: '`to` must be after `from`' })
    } else if (to - from > MAX_BOOKABLE_WINDOW_DAYS * 24 * 3600 * 1000) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `The window may span at most ${MAX_BOOKABLE_WINDOW_DAYS} days`,
      })
    }
    if (!isKnownTimeZone(input.timeZone)) {
      ctx.addIssue({ code: 'custom', path: ['timeZone'], message: 'Unknown IANA time zone' })
    }
  })

export const listBookableProviders = oc
  .route({ method: 'GET', path: '/v2/appointments/bookable-providers' })
  .input(listBookableProvidersInput)
  .output(
    z.object({
      providers: z.array(
        z.object({
          practitionerId: z.string(),
          displayName: z.string(),
          photoUrl: z.string().nullable(),
          /** Specialty codes from the practitioner's licenses, deduplicated. */
          specialties: z.array(z.string()),
          /** The patient has a completed visit (arrived / checked-in / fulfilled) with this provider. */
          seenBefore: z.boolean(),
          /** Bookable Slots in the window; `0` sorts the provider to the tail. */
          totalCount: z.number().int(),
          /** Start of the earliest Bookable Slot, ISO-8601; `null` when `totalCount` is 0. */
          nextSlotStart: z.string().nullable(),
          /** Only days with at least one Bookable Slot, ascending; `date` is `YYYY-MM-DD` in `timeZone`. */
          days: z.array(z.object({ date: z.string(), count: z.number().int() })),
        }),
      ),
    }),
  )
